import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { useAutoTour } from 'hooks/useTours';
import Icon from 'components/AppIcon';

import HeroSection from './components/HeroSection';
import ProblemSection from './components/ProblemSection';
import SolutionSection from './components/SolutionSection';
import PricingSection from './components/PricingSection';
import FeaturesSection from './components/FeaturesSection';
import TestimonialsSection from './components/TestimonialsSection';
import TrustSection from './components/TrustSection';
import SolutionOverview from './components/SolutionOverview';
import FAQSection from './components/FAQSection';
import ProcessSection from './components/ProcessSection';
import ExpertiseSection from './components/ExpertiseSection';
import GuaranteeSection from './components/GuaranteeSection';
import CTASection from './components/CTASection';
import StickyNavigation from 'components/ui/StickyNavigation';
import FloatingActionButtons from 'components/ui/FloatingActionButtons';
import MobileBottomNavigation from 'components/ui/MobileBottomNavigation';
import ProgressIndicator from 'components/ui/ProgressIndicator';
import { Sun, Moon } from 'lucide-react';

const LandingPage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  // Auto-trigger landing navigation tour for new visitors
  useAutoTour('landing-navigation', {
    delay: 3000,
    firstVisit: true,
    path: '/'
  });

  useEffect(() => {
    setIsLoaded(true);
    // Check if user previously set dark mode preference
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode) {
      setDarkMode(JSON.parse(savedDarkMode));
    }
  }, []);

  useEffect(() => {
    // Save dark mode preference to localStorage
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };


  return (
    <div className={`min-h-screen overflow-x-hidden text-text-primary ${darkMode ? 'bg-black' : 'bg-[#0A0A1A]'} relative`}>
      {/* Modern animated background */}
      <div className="fixed inset-0 z-0 overflow-hidden">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A0A1A] via-[#0F0F23] to-[#0A0A1A]"></div>

        {/* Grid pattern */}
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDIwMjAiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDM0aDR2MWgtNHYtMXptMC0yaDF2NGgtMXYtNHptMi0yaDF2MWgtMXYtMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMmgtMXYxaDF2LTF6bS0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0yLTJoMXYxaC0xdi0xem0tMiAydi0xaC0xdjFoMXptLTIgMnYtMWgtMXYxaDF6bS0yIDBoMXYxaC0xdi0xek0xMiAyMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6bTIgMGgxdjFoLTF2LTF6TTEyIDIyaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXpNMTIgMjRoNHYxaC00di0xem04IDBoNHYxaC00di0xem04IDBoNHYxaC00di0xem04IDBoNHYxaC00di0xek0xMiAyNmg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6bTggMGg0djFoLTR2LTF6TTEyIDI4aDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXptOCAwaDR2MWgtNHYtMXoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-30"></div>

        {/* Animated blobs */}
        <div className="absolute top-0 left-0 w-[800px] h-[800px] bg-primary/5 rounded-full blur-3xl animate-blob"></div>
        <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-secondary/5 rounded-full blur-3xl animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-accent/5 rounded-full blur-3xl animate-blob animation-delay-4000"></div>

        {/* Subtle noise texture */}
        <div className="absolute inset-0 opacity-20 mix-blend-overlay" style={{ 
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` 
        }}></div>

        {/* Light rays effect */}
        <div className="absolute inset-0 bg-gradient-to-t from-transparent via-primary/5 to-transparent opacity-20"></div>
      </div>

      {/* Progress Indicator */}
      <ProgressIndicator />

      {/* Sticky Navigation */}
      <StickyNavigation darkMode={darkMode} toggleDarkMode={toggleDarkMode} />
      

      {/* Main Content */}
      <main className="relative z-10">
        {/* Hero Section */}
        <HeroSection />

          {/* Solution Overview */}
          <SolutionOverview />

          {/* Process Section */}
          <ProcessSection />

          {/* Features Section */}
          {/*<FeaturesSection />*/}

          {/* Expertise Section */}
          <ExpertiseSection />

        {/* Pricing Section */}
        <PricingSection />

          {/* Guarantee Section */}
          <GuaranteeSection />



        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* FAQ Section */}
        <FAQSection />

        {/* Final CTA Section */}
        <CTASection />
      </main>

      {/* Floating Action Buttons */}
      <FloatingActionButtons />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNavigation />
    </div>
  );
};

export default LandingPage;
